<template>
  <ion-page>
    <PageHeaderWithMenu title="Загрузка медиатеки" :scrolled="headerScrolled" />
    <ion-content :class="[isMobile ? 'main-background' : 'liga-web-background']" :fullscreen="true" @ionScroll="onContentScroll">
      <ion-card class="admin-card disk-card">
        <ion-card-header>
          <ion-card-title>Место на сервере</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <p v-if="diskSpace">Свободно: {{ formatBytes(diskSpace.availableBytes) }} из {{ formatBytes(diskSpace.totalBytes) }}</p>
          <p v-else class="ion-text-wrap">Загрузка данных о месте…</p>
        </ion-card-content>
      </ion-card>

      <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Раздел медиатеки</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <ion-item>
            <ion-select label="Главный раздел" label-placement="stacked" v-model="selectedMediaCategory"
              interface="popover" mode="ios" placeholder="Выберите раздел" @ionChange="onMediaCategoryChange()">
              <ion-select-option v-for="category in mediaCategories" :key="category.key" :value="category.key">{{ category.label }}</ion-select-option>
            </ion-select>
          </ion-item>
        </ion-card-content>
      </ion-card>

      <div class="media-tab-wrap">
        <ion-segment :value="mediaTab" @ionChange="onMediaTabChange($event)" mode="ios" class="liga-glass-segment media-tab-segment">
          <ion-segment-button mode="ios" value="video">
            <ion-label>Видео</ion-label>
          </ion-segment-button>
          <ion-segment-button mode="ios" value="pdf">
            <ion-label>PDF материалы</ion-label>
          </ion-segment-button>
        </ion-segment>
      </div>

      <div v-show="mediaTab === 'video'" class="media-tab-panel">
        <AdminVideoUpload embed-mode :media-category="selectedMediaCategory" />
      </div>

      <div v-show="mediaTab === 'pdf'" class="media-tab-panel">
        <ion-card class="admin-card">
          <ion-card-header>
            <ion-card-title>Загрузить PDF</ion-card-title>
          </ion-card-header>
          <ion-card-content>
            <ion-item>
              <ion-select label="Подраздел медиатеки" label-placement="stacked" v-model="pdfUploadSectionKey"
                interface="popover" mode="ios" placeholder="Выберите раздел">
                <ion-select-option v-for="s in educationSections" :key="s.id || s.folderKey" :value="s.folderKey">{{ s.name }} ({{ s.folderKey }})</ion-select-option>
              </ion-select>
            </ion-item>
            <ion-item>
              <ion-input label="Название для пользователей" label-placement="stacked" v-model="pdfUploadDisplayName"
                placeholder="Отображаемое название материала"></ion-input>
            </ion-item>
            <ion-item>
              <input type="file" ref="pdfFileInput" accept=".pdf,application/pdf" @change="onPdfFileSelected" />
            </ion-item>
            <p v-if="selectedPdfFile" class="file-info">
              Файл: {{ selectedPdfFile.name }} ({{ formatBytes(selectedPdfFile.size) }})
              <span v-if="!isPdfExtension(selectedPdfFile.name)" class="file-too-big"> — допустимы только файлы .pdf</span>
            </p>
            <ion-button expand="block" @click="doPdfUpload()" :disabled="!canPdfUpload || pdfUploadInProgress">
              {{ pdfUploadInProgress ? 'Загрузка…' : 'Загрузить PDF' }}
            </ion-button>
          </ion-card-content>
        </ion-card>

        <ion-card class="admin-card">
          <ion-card-header>
            <ion-card-title>PDF материалы раздела «{{ mediaCategoryTitle }}»</ion-card-title>
            <p class="admin-card-download-hint">Переключатель «Скачивание» разрешает или запрещает пользователям скачивать файл к себе.</p>
          </ion-card-header>
          <ion-card-content>
            <ion-item>
              <ion-select label="Раздел" label-placement="stacked" v-model="filterPdfSectionKey" interface="popover" mode="ios"
                @ionChange="loadPdfs()">
                <ion-select-option value="">Все разделы</ion-select-option>
                <ion-select-option v-for="s in educationSections" :key="s.id || s.folderKey" :value="s.folderKey">{{ s.name }}</ion-select-option>
              </ion-select>
            </ion-item>
            <ion-list v-if="pdfs.length">
              <ion-item v-for="p in pdfs" :key="p.id" class="pdf-row">
                <ion-label>
                  <h2>{{ p.displayName }}</h2>
                  <p>{{ p.sectionName || p.sectionFolderKey }} · {{ formatBytes(p.fileSizeBytes || 0) }}</p>
                  <div class="pdf-row-end">
                    <ion-toggle
                      :checked="!!p.allowDownload"
                      @ionChange="onPdfAllowDownloadChange(p, $event)"
                      aria-label="Скачивание"
                      title="Разрешить скачивание пользователям"></ion-toggle>
                    <span class="pdf-toggle-label">Скачивание</span>
                    <div class="pdf-actions">
                      <ion-button fill="clear" color="medium" size="small" @click="renamePdf(p)" title="Переименовать">
                        <ion-icon slot="icon-only" :icon="pencilOutline"></ion-icon>
                      </ion-button>
                      <ion-button fill="clear" color="danger" size="small" @click="deletePdf(p)">
                        <ion-icon slot="icon-only" :icon="trashOutline"></ion-icon>
                      </ion-button>
                    </div>
                  </div>
                </ion-label>
              </ion-item>
            </ion-list>
            <p v-else class="empty-hint">Нет PDF материалов.</p>
          </ion-card-content>
        </ion-card>
      </div>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { EducationSection } from '@/model/education-section.model';
import { GlobalContext } from '@/model/globalcontext.model';
import { IEducationPdfMaterial } from '@/model/education-pdf-material.model';
import { DEFAULT_MEDIA_CATEGORY, MEDIA_CATEGORIES, MediaCategoryKey, mediaCategoryLabel } from '@/model/media-category.model';
import { IDiskSpace } from '@/model/video-upload.model';
import AdminService from '@/services/admin.service';
import AdminVideoUpload from '@/views/admin/AdminVideoUpload.vue';
import {
  alertController,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonSegment,
  IonSegmentButton,
  IonToggle,
  toastController,
} from '@ionic/vue';
import PageHeaderWithMenu from '@/components/PageHeaderWithMenu.vue';
import { pencilOutline, trashOutline } from 'ionicons/icons';
import { defineComponent, inject } from 'vue';

export default defineComponent({
  name: 'AdminMediaUpload',
  components: {
    AdminVideoUpload,
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonPage,
    IonSelect,
    IonSelectOption,
    IonSegment,
    IonSegmentButton,
    IonToggle,
    PageHeaderWithMenu,
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    return {
      isMobile: globalContext.isMobile,
      pencilOutline,
      trashOutline,
    };
  },
  data() {
    return {
      adminService: new AdminService(),
      diskSpace: null as IDiskSpace | null,
      educationSections: [] as EducationSection[],
      mediaCategories: MEDIA_CATEGORIES,
      selectedMediaCategory: DEFAULT_MEDIA_CATEGORY as MediaCategoryKey,
      mediaTab: 'video' as 'video' | 'pdf',
      headerScrolled: false,
      // PDF
      pdfs: [] as IEducationPdfMaterial[],
      pdfUploadSectionKey: '' as string,
      pdfUploadDisplayName: '',
      selectedPdfFile: null as File | null,
      filterPdfSectionKey: '',
      pdfUploadInProgress: false,
    };
  },
  computed: {
    mediaCategoryTitle(): string {
      return mediaCategoryLabel(this.selectedMediaCategory);
    },
    canPdfUpload(): boolean {
      if (!this.selectedPdfFile || !this.pdfUploadSectionKey || !this.pdfUploadDisplayName?.trim()) return false;
      if (!this.isPdfExtension(this.selectedPdfFile.name)) return false;
      return true;
    },
  },
  mounted() {
    this.loadDiskSpace();
    this.loadSections();
    this.loadPdfs();
  },
  methods: {
    onContentScroll(e: CustomEvent) {
      const scrollTop = e.detail?.scrollTop ?? 0;
      this.headerScrolled = scrollTop > 16;
    },
    onMediaTabChange(e: CustomEvent) {
      const value = e.detail?.value as string | undefined;
      if (value === 'video' || value === 'pdf') {
        this.mediaTab = value;
      }
    },
    onMediaCategoryChange() {
      this.pdfUploadSectionKey = '';
      this.filterPdfSectionKey = '';
      this.loadSections();
      this.loadPdfs();
    },
    formatBytes(bytes: number): string {
      if (bytes === 0) return '0 Б';
      const k = 1024;
      const sizes = ['Б', 'КБ', 'МБ', 'ГБ'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
    },
    isPdfExtension(fileName: string): boolean {
      const ext = (fileName || '').split('.').pop()?.toLowerCase();
      return ext === 'pdf';
    },
    async loadDiskSpace() {
      try {
        this.diskSpace = await this.adminService.getDiskSpace();
      } catch (e) {
        console.error('loadDiskSpace', e);
      }
    },
    async loadSections() {
      try {
        this.educationSections = await this.adminService.getEducationSections(this.selectedMediaCategory);
        if (this.educationSections.length && !this.pdfUploadSectionKey) {
          this.pdfUploadSectionKey = this.educationSections[0].folderKey;
        }
      } catch (e) {
        console.error('loadSections', e);
      }
    },
    onPdfFileSelected(ev: Event) {
      const input = ev.target as HTMLInputElement;
      const file = input.files?.[0] || null;
      this.selectedPdfFile = file;
      if (file && !this.isPdfExtension(file.name)) {
        (this as { selectedPdfFile: File | null }).selectedPdfFile = null;
        input.value = '';
      }
    },
    async loadPdfs() {
      try {
        this.pdfs = await this.adminService.getEducationPdfs(
          this.filterPdfSectionKey || undefined,
          this.selectedMediaCategory
        );
      } catch (e) {
        console.error('loadPdfs', e);
      }
    },
    async doPdfUpload() {
      if (!this.canPdfUpload || !this.selectedPdfFile) return;
      this.pdfUploadInProgress = true;
      try {
        await this.adminService.uploadPdf(
          this.selectedPdfFile,
          this.pdfUploadSectionKey,
          this.pdfUploadDisplayName.trim(),
          this.selectedMediaCategory
        );
        const toast = await toastController.create({
          message: 'PDF загружен.',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        this.pdfUploadDisplayName = '';
        this.selectedPdfFile = null;
        const input = this.$refs.pdfFileInput as HTMLInputElement | undefined;
        if (input) input.value = '';
        this.loadDiskSpace();
        this.loadPdfs();
      } catch (e: unknown) {
        console.error('doPdfUpload', e);
        const err = e as Error & { response?: { status?: number } };
        const message = err.message?.includes('расширением') ? err.message
          : (err.response?.status === 400 ? 'Допустимы только файлы .pdf' : 'Ошибка загрузки PDF');
        const toast = await toastController.create({
          message,
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.pdfUploadInProgress = false;
      }
    },
    async onPdfAllowDownloadChange(pdf: IEducationPdfMaterial, event: CustomEvent) {
      const checked = !!event.detail?.checked;
      try {
        await this.adminService.updateEducationPdf(pdf.id, { allowDownload: checked });
        pdf.allowDownload = checked;
        const toast = await toastController.create({
          message: checked ? 'Скачивание разрешено' : 'Скачивание отключено',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
      } catch (e) {
        console.error('onPdfAllowDownloadChange', e);
        const toast = await toastController.create({
          message: 'Ошибка сохранения',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      }
    },
    async renamePdf(pdf: IEducationPdfMaterial) {
      const alert = await alertController.create({
        header: 'Переименовать материал',
        cssClass: 'alert-dark-theme',
        inputs: [{ name: 'displayName', type: 'text', value: pdf.displayName, placeholder: 'Новое название' }],
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Сохранить',
            handler: async (data: { displayName?: string }) => {
              const newName = (data.displayName || '').trim();
              if (!newName) return false;
              try {
                await this.adminService.updateEducationPdf(pdf.id, { displayName: newName });
                const toast = await toastController.create({
                  message: 'Название обновлено.',
                  duration: 2000,
                  position: 'top',
                  color: 'success',
                });
                await toast.present();
                this.loadPdfs();
              } catch (e) {
                console.error('renamePdf', e);
                const toast = await toastController.create({
                  message: 'Ошибка при переименовании.',
                  duration: 2000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
              }
              return true;
            },
          },
        ],
      });
      await alert.present();
    },
    async deletePdf(pdf: IEducationPdfMaterial) {
      const alert = await alertController.create({
        header: 'Удалить материал?',
        message: `Удалить «${pdf.displayName}»? Файл на сервере будет удалён.`,
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Удалить',
            role: 'destructive',
            handler: async () => {
              try {
                await this.adminService.deleteEducationPdf(pdf.id);
                const toast = await toastController.create({
                  message: 'PDF удалён.',
                  duration: 2000,
                  position: 'top',
                  color: 'success',
                });
                await toast.present();
                this.loadPdfs();
                this.loadDiskSpace();
              } catch (e) {
                console.error('deletePdf', e);
                const toast = await toastController.create({
                  message: 'Ошибка удаления.',
                  duration: 3000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
              }
            },
          },
        ],
      });
      await alert.present();
    },
  },
});
</script>

<style scoped>
.admin-card {
  margin: 16px;
  --background: var(--liga-float-glass-bg-strong);
}

.admin-card ion-card-title {
  color: var(--liga-chrome-text);
}

.admin-card-download-hint {
  color: var(--liga-chrome-text-muted);
  font-size: 0.85rem;
  margin-top: 4px;
  white-space: normal;
}

.pdf-toggle-label {
  font-size: 0.85rem;
  color: var(--liga-chrome-text);
  white-space: nowrap;
}

.admin-card ion-card-content {
  color: var(--liga-chrome-text);
}

.admin-card ion-item,
.admin-card ion-list,
.admin-card ion-list ion-item {
  --background: transparent !important;
  --background-activated: transparent !important;
  --background-hover: transparent !important;
  --background-focused: transparent !important;
  --color: var(--liga-chrome-text);
}
.admin-card ion-list {
  background: transparent !important;
}
.admin-card ion-list ion-item::part(native),
.admin-card ion-item::part(native) {
  --background: transparent !important;
  background: transparent !important;
}

.disk-card p {
  margin: 0;
}

.media-tab-wrap {
  margin: 0 16px 12px;
}

.media-tab-segment {
  margin-left: 0;
  margin-right: 0;
  width: 100%;
  max-width: 100%;
}

.media-tab-panel {
  margin-top: 0;
}

.file-info {
  margin: 8px 0;
}

.file-too-big {
  color: var(--ion-color-danger);
}

.empty-hint {
  color: var(--liga-chrome-text-muted);
}

.pdf-row,
.admin-card .pdf-row {
  --background: transparent !important;
  --background-activated: transparent !important;
  --background-hover: transparent !important;
  --background-focused: transparent !important;
}
.pdf-row::part(native),
.admin-card .pdf-row::part(native) {
  --background: transparent !important;
  background: transparent !important;
}

.pdf-row ion-label h2 {
  color: var(--liga-chrome-text);
}

.pdf-row ion-label p {
  color: var(--liga-chrome-text-muted);
}

.pdf-row-end {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.pdf-row ion-label {
  white-space: normal;
}

@media (max-width: 768px) {
  .admin-card .pdf-row {
    min-height: auto;
    align-items: flex-start;
    --padding-start: 12px;
    --padding-end: 12px;
    --inner-padding-end: 12px;
    padding-top: 10px;
    padding-bottom: 10px;
  }
  .pdf-row ion-label h2 {
    font-size: 0.95rem;
    word-break: break-word;
  }
  .pdf-row ion-label p {
    font-size: 0.8rem;
  }
  .pdf-row .pdf-row-end {
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px solid rgba(255, 255, 255, 0.15);
    width: 100%;
    gap: 6px;
  }
  .pdf-row .pdf-toggle-label {
    margin-right: 0;
    font-size: 0.85rem;
  }
  .pdf-row .pdf-actions {
    margin-left: auto;
  }
}

.pdf-actions {
  display: flex;
  gap: 4px;
}

.pdf-actions ion-button {
  --padding-start: 4px;
  --padding-end: 4px;
}

input[type="file"] {
  color: var(--liga-chrome-text);
}

.admin-card ion-button:not([fill="clear"]) {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --background-hover: var(--ion-color-orange-tint);
  --color: var(--liga-chrome-text);
  font-weight: bold;
  --border-radius: 14px;
  margin-top: 8px;
}

.admin-card ion-button[fill="clear"] {
  --color: var(--ion-color-orange);
  font-weight: 600;
}
</style>
